<script setup lang="ts">
import { unwrapArray } from '@sortera/ee-utils'
import _clone from 'lodash/clone.js'
import { computed } from 'vue'

import { UiAvatar } from '@/components/ui/UiAvatar/index.ts'
import { ROUTE_NAMES, UiRouterLink, useRoute } from '@/services/routing/index.ts'
import { useAppTitles } from '@/services/use-app-titles.ts'
import { useAuth } from '@/services/use-auth.ts'
import { useUser } from '@/services/use-user.ts'

import { FeatHeadwayWidget } from '../FeatHeadwayWidget/index.ts'

import FeatNavbarFailureNotifications from './FeatNavbarFailureNotifications.vue'

const { user } = useUser()
const route = useRoute()
const { navbarTitle } = useAppTitles()

const historyLength = computed(() => _clone(window.history.length))
const isFacilityIdInParams = computed(() => !!unwrapArray(route.params.facilityId))

function dropdownItemClick(): void {
  const dropdownItem = document.activeElement
  if (!(dropdownItem instanceof HTMLElement)) return

  dropdownItem.blur()
}

function signOut(): Promise<void> {
  return useAuth().logout()
}
</script>

<template>
  <div class="navbar bg-neutral text-neutral-content mb-2 shadow-lg">
    <button
      v-if="$route.name !== 'home' && historyLength > 1"
      class="btn btn-square btn-ghost"
      tabindex="0"
      @click="$router.back()"
    >
      <svg
        class="text-success inline-block h-6 w-6 stroke-current"
        fill="none"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 19l-7-7m0 0l7-7m-7 7h18"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
        />
      </svg>
    </button>
    <div class="mx-2 flex-1 px-2">
      <span class="font-mon text-lg leading-none font-bold">
        {{ navbarTitle }}
      </span>
    </div>
    <div class="flex items-stretch gap-1 sm:gap-3">
      <FeatNavbarFailureNotifications />
      <FeatHeadwayWidget
        id="changelog"
        account="JnQDO7"
      >
        <div class="flex items-center" />
      </FeatHeadwayWidget>
      <div class="dropdown dropdown-end">
        <div
          class="btn btn-circle btn-ghost p-0"
          role="button"
          tabindex="0"
        >
          <UiAvatar
            class="h-10 w-10"
            :name="
              user?.name ??
              // Needs some content so that the avatar is always visible.
              // Important so that users can always find the menu options.
              '?'
            "
          />
        </div>
        <ul
          class="menu dropdown-content rounded-box bg-base-100 text-base-content z-1 min-w-60 border border-gray-200 shadow-sm"
          tabindex="0"
        >
          <li
            v-if="user?.featureFlags?.developer"
            @click="dropdownItemClick"
          >
            <UiRouterLink :to="{ name: ROUTE_NAMES.DEV }">Dev page</UiRouterLink>
          </li>
          <li @click="dropdownItemClick">
            <a href="mailto:fv@sortera.se">
              {{ $t('nav-menu.create-ticket') }}
            </a>
          </li>

          <li
            v-if="user?.roles?.workLeader || user?.roles?.admin"
            @click="dropdownItemClick"
          >
            <UiRouterLink :to="{ name: ROUTE_NAMES.CONVERT }">
              {{ $t('nav-menu.convert-dumped') }}
            </UiRouterLink>
          </li>

          <li
            v-if="user?.roles?.admin && isFacilityIdInParams"
            @click="dropdownItemClick"
          >
            <UiRouterLink :to="{ name: ROUTE_NAMES.MANAGE_STATION }">
              {{ $t('station.manage.title') }}
            </UiRouterLink>
          </li>

          <li
            v-if="user?.roles?.admin"
            @click="dropdownItemClick"
          >
            <UiRouterLink :to="{ name: ROUTE_NAMES.ADMIN }">
              {{ $t('nav-menu.admin') }}
            </UiRouterLink>
          </li>
          <li @click="dropdownItemClick">
            <UiRouterLink :to="{ name: ROUTE_NAMES.SETTINGS }">
              {{ $t('nav-menu.settings') }}
            </UiRouterLink>
          </li>
          <div class="divider my-0" />
          <li @click="dropdownItemClick">
            <a
              class="flex items-center justify-between gap-2"
              @click="signOut"
            >
              {{ $t('auth.signout') }}
              <svg
                class="inline-block h-5 w-5 stroke-current"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                  fill-rule="evenodd"
                />
              </svg>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
